// packages/chatbot/src/components/MessageBubble.tsx

import { Card } from '@dopomogai/ui';
import { User, Bot } from 'lucide-react';
import type { UIMessage } from '../types';
import type { CSSProperties, ReactNode } from 'react';
import { getContrastingTextColor } from '../utils/colorUtils';
import { parseMarkdown } from '../utils/messageFormatter';

interface MessageBubbleProps {
  message: UIMessage;
  userAvatar?: ReactNode | string;
  agentAvatar?: ReactNode | string;
  userMessageStyle?: CSSProperties;
  assistantMessageStyle?: CSSProperties;
}

export function MessageBubble({
  message,
  userAvatar,
  agentAvatar,
  userMessageStyle,
  assistantMessageStyle,
}: MessageBubbleProps) {
  if (message.type !== 'user' && message.type !== 'assistant') return null;

  const isUser = message.type === 'user';

  const renderAvatar = () => {
    const avatarSrc = isUser ? userAvatar : agentAvatar;
    const DefaultIcon = isUser ? User : Bot;
    const defaultBg = isUser ? 'bg-slate-600' : 'bg-emerald-500';

    if (typeof avatarSrc === 'string') {
      return <img src={avatarSrc} alt={isUser ? 'User' : 'Agent'} className="w-8 h-8 rounded-lg object-cover" />;
    }
    if (avatarSrc) return avatarSrc;
    return (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${defaultBg}`}>
        <DefaultIcon className="w-4 h-4 text-white" />
      </div>
    );
  };

  const userBg = userMessageStyle?.backgroundColor || '#3B82F6';
  const assistantBg = assistantMessageStyle?.backgroundColor || '#F1F5F9';

  const bubbleStyles = isUser
    ? {
        ...userMessageStyle,
        backgroundColor: userBg,
        color: userMessageStyle?.color || getContrastingTextColor(userBg),
      }
    : {
        ...assistantMessageStyle,
        backgroundColor: assistantBg,
        color: assistantMessageStyle?.color || getContrastingTextColor(assistantBg),
      };

  let htmlContent = parseMarkdown(message.content);
  if (message.type === 'assistant' && message.isStreaming) {
    htmlContent += '<span class="inline-block w-2 h-4 bg-slate-400 animate-pulse ml-1 align-middle"></span>';
  }

  return (
    <div className={`flex gap-3 w-full max-w-[85%] ${isUser ? 'ml-auto justify-end' : 'mr-auto justify-start'}`}>
      {/* Show avatar only for the assistant; user messages have no avatar */}
      {!isUser && renderAvatar()}

      <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        <Card
          noPadding
          className={`border-2 border-black shadow-none mb-0 ${isUser ? 'user-message' : 'assistant-message'} cursor-default select-text`}
          style={bubbleStyles}
        >
          <div
            className="p-3 text-sm break-words"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </Card>
      </div>
    </div>
  );
}
